package com.exura.pages.publicplans

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.exura.features.publicplans.domain.model.AnonymousPublicPlanOrder
import com.exura.features.publicplans.domain.model.ProviderPublicPlan
import com.exura.features.publicplans.domain.model.PublicPlanServingEntry
import com.exura.features.publicplans.domain.usecase.DisablePublicPlanSharingUseCase
import com.exura.features.publicplans.domain.usecase.EnablePublicPlanSharingUseCase
import com.exura.features.publicplans.domain.usecase.GetPublicPlanOrdersUseCase
import com.exura.features.publicplans.domain.usecase.GetPublicPlanServingsUseCase
import com.exura.features.publicplans.domain.usecase.GetPublicPlanUseCase
import com.exura.features.publicplans.domain.usecase.RefreshPlanStatisticsUseCase
import com.exura.features.publicplans.domain.usecase.ReplacePlanDefinitionUseCase
import com.exura.navigation.Route
import com.exura.util.injectAndRemember
import com.varabyte.kobweb.core.PageContext
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.launch

sealed interface PublicPlanDetailsEvent {
    data object RefreshStats : PublicPlanDetailsEvent
    data object ToggleSharing : PublicPlanDetailsEvent
    data object GoBack : PublicPlanDetailsEvent
    data class ReplacePlan(val newSourceId: String) : PublicPlanDetailsEvent
}

data class PublicPlanDetailsUiState(
    val isLoading: Boolean = true,
    val error: String? = null,
    val plan: ProviderPublicPlan? = null,
    val orders: List<AnonymousPublicPlanOrder> = emptyList(),
    val servings: List<PublicPlanServingEntry> = emptyList(),
)

class PublicPlanDetailsController(
    private val planId: String,
    private val scope: CoroutineScope,
    private val context: PageContext,
    private val getPublicPlanUseCase: GetPublicPlanUseCase,
    private val disableSharingUseCase: DisablePublicPlanSharingUseCase,
    private val enableSharingUseCase: EnablePublicPlanSharingUseCase,
    private val refreshStatisticsUseCase: RefreshPlanStatisticsUseCase,
    private val replaceDefinitionUseCase: ReplacePlanDefinitionUseCase,
    private val getPublicPlanOrdersUseCase: GetPublicPlanOrdersUseCase,
    private val getPublicPlanServingsUseCase: GetPublicPlanServingsUseCase,
) {
    var uiState by mutableStateOf(PublicPlanDetailsUiState())
        private set

    private var ordersJob: Job? = null
    private var servingsJob: Job? = null

    init {
        loadPlanDetails()
    }

    fun loadPlanDetails() {
        scope.launch {
            if (uiState.plan == null) {
                uiState = uiState.copy(isLoading = true, error = null)
            }
            getPublicPlanUseCase(planId)
                .onSuccess { plan ->
                    uiState = uiState.copy(isLoading = false, plan = plan)
                    if (ordersJob == null) observeOrders()
                    if (servingsJob == null) observeServings()
                }
                .onFailure { error ->
                    uiState = uiState.copy(isLoading = false, error = error.message)
                }
        }
    }

    private fun observeOrders() {
        ordersJob?.cancel()
        ordersJob = scope.launch {
            getPublicPlanOrdersUseCase(planId)
                .onSuccess { flow ->
                    flow.collect { orders ->
                        uiState = uiState.copy(orders = orders)
                    }
                }
                .onFailure {
                    console.log("Error loading orders: ${it.message}")
                }
        }
    }

    private fun observeServings() {
        servingsJob?.cancel()
        servingsJob = scope.launch {
            getPublicPlanServingsUseCase(planId)
                .onSuccess { flow ->
                    flow.collect { servings ->
                        uiState = uiState.copy(servings = servings)
                    }
                }
                .onFailure {
                    console.log("Error loading servings: ${it.message}")
                }
        }
    }

    fun onRefreshStats() {
        scope.launch {
            uiState = uiState.copy(isLoading = true)
            refreshStatisticsUseCase(planId)
                .onSuccess {
                    loadPlanDetails()
                }
                .onFailure {
                    uiState = uiState.copy(isLoading = false)
                }
        }
    }

    fun onToggleSharing() {
        val currentPlan = uiState.plan ?: return
        val newStatus = !currentPlan.isSharingEnabled

        scope.launch {
            uiState = uiState.copy(isLoading = true)
            val result = if (newStatus) enableSharingUseCase(planId) else disableSharingUseCase(planId)

            result.onSuccess {
                loadPlanDetails()
            }.onFailure {
                uiState = uiState.copy(isLoading = false)
            }
        }
    }

    fun onReplacePlanDefinition(newPlanSourceId: String) {
        scope.launch {
            uiState = uiState.copy(isLoading = true)
            replaceDefinitionUseCase(planId, newPlanSourceId)
                .onSuccess { loadPlanDetails() }
                .onFailure { error ->
                    uiState = uiState.copy(isLoading = false, error = error.message)
                }
        }
    }

    fun onBackClicked() {
        context.router.navigateTo(Route.PUBLIC_PLAN)
    }


    fun handleEvent(event: PublicPlanDetailsEvent) {
        when (event) {
            is PublicPlanDetailsEvent.GoBack -> onBackClicked()
            is PublicPlanDetailsEvent.RefreshStats -> onRefreshStats()
            is PublicPlanDetailsEvent.ToggleSharing -> onToggleSharing()
            is PublicPlanDetailsEvent.ReplacePlan -> onReplacePlanDefinition(event.newSourceId)
        }
    }
}

@Composable
fun rememberPublicPlanDetailsController(
    scope: CoroutineScope,
    context: PageContext,
    planId: String,
): PublicPlanDetailsController {
    val getPublicPlan = injectAndRemember<GetPublicPlanUseCase>()
    val disableSharing = injectAndRemember<DisablePublicPlanSharingUseCase>()
    val enableSharing = injectAndRemember<EnablePublicPlanSharingUseCase>()
    val refreshStats = injectAndRemember<RefreshPlanStatisticsUseCase>()
    val replacePlan = injectAndRemember<ReplacePlanDefinitionUseCase>()
    val getOrders = injectAndRemember<GetPublicPlanOrdersUseCase>()
    val getServings = injectAndRemember<GetPublicPlanServingsUseCase>()

    return remember(scope, context, planId) {
        PublicPlanDetailsController(
            planId = planId,
            scope = scope,
            context = context,
            getPublicPlanUseCase = getPublicPlan,
            disableSharingUseCase = disableSharing,
            enableSharingUseCase = enableSharing,
            refreshStatisticsUseCase = refreshStats,
            replaceDefinitionUseCase = replacePlan,
            getPublicPlanOrdersUseCase = getOrders,
            getPublicPlanServingsUseCase = getServings,
        )
    }
}
